import { loadStaffOptions, loadUnitOptions } from '@api/loadOptions'
import { useAdmissionOrder } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { admissionSchema } from '@schema/pDirectory.schema'
import { AdmissionDataMock } from '@type/admission.type'
import { FormButton, Modal, TextAreaField } from '@ui'
import { FormSelect } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { Box, CheckBox, Form } from 'grommet'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { InferType } from 'yup'

type Props = {
  onClose: () => void
  defaultData?: AdmissionDataMock
}

const AddAdmissionOrder = ({ onClose, defaultData }: Props) => {
  const methods = useForm({
    resolver: yupResolver(admissionSchema),
    defaultValues: {
      care_provider: defaultData?.relationships?.care_provider?.data?.id || '',
      note: defaultData?.attributes?.note || '',
      telemetry: defaultData?.attributes?.telemetry || false,
      unit: defaultData?.relationships?.unit?.data?.id || ''
    }
  })
  const { patientId, encounterId } = useParams<string>()
  const { createAdmissionOrder, updateAdmissionOrder } = useAdmissionOrder({ patientId: patientId || '' })
  const [staffDefaultOptions, setStaffDefaultOptions] = useState<{ label: string; value: string }[]>([])
  const [unitDefaultOptions, setUnitDefaultOptions] = useState<{ label: string; value: string }[]>([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [staffs, units] = await Promise.all([loadStaffOptions(''), loadUnitOptions('')])
        if (cancelled) return
        setStaffDefaultOptions(staffs)
        setUnitDefaultOptions(units)
      } catch (e) {
        getError(e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const onSubmit = async (data: InferType<typeof admissionSchema>) => {
    const { care_provider, unit, ...attributes } = data
    const payload = {
      data: {
        attributes: {
          ...attributes
        },
        relationships: {
          care_provider: {
            data: {
              id: care_provider,
              type: 'Staff'
            }
          },
          encounter: {
            data: {
              id: encounterId,
              type: 'encounter'
            }
          },
          ...(patientId && {
            patient: {
              data: {
                id: patientId,
                type: 'Patient'
              }
            }
          }),
          unit: {
            data: {
              id: unit,
              type: 'Unit'
            }
          }
        },
        type: 'admission_order'
      }
    }

    try {
      defaultData?.id
        ? await updateAdmissionOrder({ formVal: payload, id: defaultData.id })
        : await createAdmissionOrder(payload)
      showToast(`Order ${defaultData?.id ? 'edited' : 'added'} successfully`)
      onClose()
    } catch (error) {
      getError(error)
    }
  }
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Admission Order">
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box className="w-screen md:w-full max-w-[500px]" gap={'24px'} overflow={'auto'} pad={'medium'}>
            <Box className="col-span-1" flex={false}>
              <FormSelect
                cacheOptions
                defaultOptions={staffDefaultOptions}
                isAsync
                label="Assign to"
                loadOptions={inputValue => loadStaffOptions(inputValue, { exclude_roles: 'administrator,super_admin' })}
                name="care_provider"
                options={staffDefaultOptions}
                placeholder="Select care provider"
              />
            </Box>
            <Box className="col-span-1" flex={false}>
              <FormSelect
                cacheOptions
                defaultOptions={unitDefaultOptions}
                isAsync
                label="Unit"
                loadOptions={loadUnitOptions}
                name="unit"
                options={unitDefaultOptions}
                placeholder="Select a unit"
              />
            </Box>
            <Box className="col-span-1" flex={false}>
              <Controller
                control={methods.control}
                defaultValue={false}
                name="telemetry"
                render={({ field }) => (
                  <Box align="center" direction="row" gap="small">
                    <CheckBox
                      checked={field?.value}
                      label="Telemetry"
                      onChange={e => {
                        field.onChange(e.target.checked)
                      }}
                    />
                  </Box>
                )}
              />
            </Box>
            <Box className="col-span-1" flex={false}>
              <TextAreaField label="Note" name="note" placeholder="Enter note" />
            </Box>
          </Box>
          <FormButton
            isLoading={methods.formState.isSubmitting}
            onClose={onClose}
            submit={defaultData?.id ? 'Update' : 'Order'}
          />
        </Form>
      </Modal>
    </FormProvider>
  )
}

export default AddAdmissionOrder
